import React, { useState, useMemo, useCallback } from "react";
import { Button } from "../../../components/ui";
import UserDropdown from "../../../components/ui/UserDropdown";
import { X } from "lucide-react";
import { useDispatch } from "react-redux";
import { bulkCreateUserNotifications } from "../../../redux/Slices/UserNotificationSlice";
import { 
    NotificationPriorityEnum, 
    NotificationTypeEnum, 
    UserNotificationCreateDto 
} from "../../../modules/shared/model";
import { GlobalService } from "../../../modules";

interface NotificationModalProps {
    isOpen: boolean;
    onClose: () => void;
    client: any;
    roles?: Array<{ id: number }>;
}

const NotificationModal = ({ isOpen, onClose, client, roles }: NotificationModalProps) => {
    const dispatch = useDispatch();
    const [notificationType, setNotificationType] = useState("banner");
    const [notificationTitle, setNotificationTitle] = useState("");
    const [notificationMessage, setNotificationMessage] = useState("");
    const [notificationPriority, setNotificationPriority] = useState("normal");
    const [expiryTime, setExpiryTime] = useState("");
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Memoize roles array - use provided roles or default to client role
    const clientRoles = useMemo(() => {
        return roles && roles.length > 0 ? roles : [{ id: 2 },{ id: 3 },{ id: 4 }];
    }, [roles]);
    
    // Memoize callback functions to prevent unnecessary re-renders
    const handleUsersSelect = useCallback((users: any[]) => {
        setSelectedUsers(users);
    }, []);
    
    const handleUserSelect = useCallback(() => {
        // Required prop but not used in multi-select mode
    }, []);

    if (!isOpen || !client) return null;

    // Helper function to get enum values
    const getPriorityEnum = (priority: string) => {
        switch (priority) {
            case "low": return NotificationPriorityEnum.Low;
            case "normal": return NotificationPriorityEnum.Normal;
            case "high": return NotificationPriorityEnum.High;
            case "urgent": return NotificationPriorityEnum.High; // Map urgent to high
            default: return NotificationPriorityEnum.Normal;
        }
    };

    const getTypeEnum = (type: string) => {
        switch (type) {
            case "banner": return NotificationTypeEnum.Banner;
            case "popup": return NotificationTypeEnum.Popup;
            case "message": return NotificationTypeEnum["In-App"];
            case "email": return NotificationTypeEnum.Email;
            default: return NotificationTypeEnum["In-App"];
        }
    };

    const handleSendNotification = async () => {
        if (!notificationTitle.trim() || !notificationMessage.trim()) {
            GlobalService.showToast("error", "Error", "Please fill in all required fields");
            return;
        }

        if (selectedUsers.length === 0) {
            GlobalService.showToast("error", "Error", "Please select at least one user");
            return;
        }

        setIsSubmitting(true);

        try {
            const notifications: UserNotificationCreateDto[] = selectedUsers.map(user => ({
                title: notificationTitle,
                message: notificationMessage,
                priority: { id: getPriorityEnum(notificationPriority) },
                type: { id: getTypeEnum(notificationType) },
                user: { id: user.id, email: user.email },
                expiryTime: expiryTime || undefined
            }));

            await dispatch(bulkCreateUserNotifications(notifications) as any).unwrap();
            
            GlobalService.showToast("success", "Success", `Notifications sent to ${selectedUsers.length} user(s)`);
            
            // Reset form
            setNotificationTitle("");
            setNotificationMessage("");
            setNotificationPriority("normal");
            setNotificationType("banner");
            setExpiryTime("");
            setSelectedUsers([]);
            
            onClose();
        } catch (error: any) {
            GlobalService.showToast("error", "Error", error.message || "Failed to send notifications");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4 md:p-6" style={{ textAlign: "left" }}>
            {/* Modal Container - Responsive widths and max heights */}
            <div className="bg-white rounded-lg sm:rounded-xl w-full max-w-[95vw] sm:max-w-md md:max-w-lg lg:max-w-xl max-h-[95vh] sm:max-h-[90vh] flex flex-col overflow-hidden">
                {/* Header - Responsive padding and text size */}
                <div className="p-4 sm:p-5 md:p-6 border-b flex-shrink-0">
                    <div className="flex justify-between items-start gap-3">
                        <h2 className="text-base sm:text-lg md:text-xl font-semibold leading-tight pr-2">
                            Send Notification to {client.firstName} {client.lastName}
                        </h2>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={onClose}
                            className="flex-shrink-0 -mr-2 sm:-mr-1"
                        >
                            <X className="h-5 w-5 sm:h-5 sm:w-5" />
                        </Button>
                    </div>
                </div>

                {/* Form Content - Scrollable with responsive padding and spacing */}
                <div className="p-4 sm:p-5 md:p-6 space-y-3 sm:space-y-4 overflow-y-auto flex-1">
                    {/* Select Recipients */}
                    <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                            Select Recipients
                        </label>
                        <UserDropdown
                            selectedUsers={selectedUsers}
                            onUsersSelect={handleUsersSelect}
                            onUserSelect={handleUserSelect}
                            multiSelect={true}
                            placeholder="Select users to notify..."
                            roles={clientRoles}
                            className="w-full text-sm sm:text-base"
                        />
                        {selectedUsers.length > 0 && (
                            <p className="text-xs sm:text-sm text-gray-600 mt-1.5">
                                {selectedUsers.length} user(s) selected
                            </p>
                        )}
                    </div>

                    {/* Notification Type */}
                    <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                            Notification Type
                        </label>
                        <select
                            value={notificationType}
                            onChange={(e) => setNotificationType(e.target.value)}
                            className="w-full px-3 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="banner">Banner</option>
                            <option value="popup">Popup</option>
                            <option value="message">In-app Message</option>
                            <option value="email">Email</option>
                        </select>
                    </div>

                    {/* Title */}
                    <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                            Title *
                        </label>
                        <input
                            type="text"
                            value={notificationTitle}
                            onChange={(e) => setNotificationTitle(e.target.value)}
                            placeholder="Enter notification title"
                            className="w-full px-3 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            required
                        />
                    </div>

                    {/* Message */}
                    <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                            Message *
                        </label>
                        <textarea
                            value={notificationMessage}
                            onChange={(e) => setNotificationMessage(e.target.value)}
                            placeholder="Enter notification message"
                            rows={3}
                            className="w-full px-3 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none sm:rows-4"
                            required
                        />
                    </div>

                    {/* Priority */}
                    <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                            Priority
                        </label>
                        <select
                            value={notificationPriority}
                            onChange={(e) => setNotificationPriority(e.target.value)}
                            className="w-full px-3 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="low">Low</option>
                            <option value="normal">Normal</option>
                            <option value="high">High</option>
                            <option value="urgent">Urgent</option>
                        </select>
                    </div>

                    {/* Expiry Time */}
                    <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                            Expiry Time (Optional)
                        </label>
                        <input
                            type="datetime-local"
                            value={expiryTime}
                            onChange={(e) => setExpiryTime(e.target.value)}
                            className="w-full px-3 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>
                </div>

                {/* Footer - Responsive button layout */}
                <div className="p-4 sm:p-5 md:p-6 border-t flex-shrink-0 flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3">
                    <Button 
                        variant="outline" 
                        onClick={onClose}
                        disabled={isSubmitting}
                        className="w-full sm:w-auto text-sm sm:text-base py-2 sm:py-2"
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleSendNotification}
                        disabled={isSubmitting}
                        className="w-full sm:w-auto flex items-center justify-center gap-2 text-sm sm:text-base py-2 sm:py-2"
                    >
                        {isSubmitting ? (
                            <>
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                <span className="hidden xs:inline">Sending...</span>
                                <span className="inline xs:hidden">Sending...</span>
                            </>
                        ) : (
                            <>
                                <span className="hidden sm:inline">Send to {selectedUsers.length} user(s)</span>
                                <span className="inline sm:hidden">Send ({selectedUsers.length})</span>
                            </>
                        )}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default NotificationModal;